import { PropsWithChildren, useState } from 'react';
import { User, UserProps } from '../types/user_types.ts';
import AzureAuthContext from './azure_auth_context.ts';
import {
    ApiTokenRetrievalError,
    isApiTokenExpired,
    logoutFromAPI,
} from '../utils/di_api_functions.ts';
import {
    AuthMeIdTokenExpired,
    AuthMeNotAvailable,
    AuthMeStatus,
    AuthMeUnauthenticated,
    callAadLogin,
    fetchUserProps,
    GetAzAuthMeStatus,
} from '../utils/az_auth_functions.ts';

export const AzureAuthProvider = ({ children }: PropsWithChildren) => {
    let cachedUser = JSON.parse(
        localStorage.getItem('driver-index-user') || '{}',
    ) as UserProps;

    if (isApiTokenExpired(cachedUser.tokenExpiration)) {
        // Token expired, ignore cached user
        cachedUser = {} as UserProps;
        localStorage.removeItem('driver-index-user');
    }

    const [userProps, setUserProps] = useState<UserProps>(cachedUser);

    const login = async () => {
        try {
            const azAuthState: AuthMeStatus = await GetAzAuthMeStatus();
            if (azAuthState == AuthMeStatus.UNAUTHENTICATED) {
                await callAadLogin();
            } else if (azAuthState == AuthMeStatus.AUTHENTICATED) {
                await syncUserInformation();
            } else {
                alert(
                    `Login currently unavailable (state_code: ${azAuthState})`,
                );
            }
        } catch {
            alert('Login currently unavailable (unknown error)');
        }
    };

    const syncUserInformation = async (): Promise<boolean> => {
        // if user information is already complete, return success
        if (userProps.token && !isApiTokenExpired(userProps.tokenExpiration))
            return true;

        let newUserProps = {} as UserProps;
        try {
            // Attempt retrieval of user props
            newUserProps = await fetchUserProps();
            localStorage.setItem(
                'driver-index-user',
                JSON.stringify(newUserProps),
            );
        } catch (e) {
            if (e instanceof AuthMeUnauthenticated)
                return false; // simply not logged in
            else if (e instanceof AuthMeNotAvailable)
                console.info('AuthMe not available.');
            else if (e instanceof ApiTokenRetrievalError)
                console.error('Error retrieving API token');
            else if (e instanceof AuthMeIdTokenExpired)
                console.info('AuthMe token expired.');
            else
                console.error(
                    'Unexpected error during retrieval of user props',
                );

            return false;
        }

        setUserProps(newUserProps);
        return true;
    };

    const logout = () => {
        logoutFromAPI(user.userProps.token).catch();
        setUserProps({} as UserProps);
        localStorage.removeItem('driver-index-user');
    };

    const user: User = {
        userProps: userProps,
        login,
        syncUserInformation,
        logout,
    };

    return (
        <AzureAuthContext.Provider value={user}>
            {children}
        </AzureAuthContext.Provider>
    );
};
